import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  baldeDaOperacao,
  operacaoDoCartao,
  operacaoEhLancamento,
  type CompetenciaLancamento,
  type OperacaoMargem,
  type ProdutoCartao,
  type ReservaCartao,
  type TipoMargem,
} from "@atlas/types";
import { rotuloCtaBucketAverbacao, rotuloCtaBucketCartao } from "./averbar-bucket-cta";
import { expiracaoDoContrato } from "../../../lib/expiracao-contrato";
import { MARGEM_MINIMA, aplicarReservaNaFicha, disponivelUtil } from "./reserva-na-ficha";
import { cpfBuscadoDo } from "./cpf-buscado";
import {
  HistoricoLancamentos,
  SelectOperacaoMargem,
  caixaAviso,
  modalBackdrop,
  modalCard,
  operacaoDoCard,
  reservaAtivaDoBalde,
  rotuloCompetencia,
  rotuloCorte,
} from "./operacao-cartao";

// Limites regulatórios da margem consignável — replicados aqui para não puxar
// @atlas/domain (que não é dep do @atlas/web). Fonte da verdade continua no
// domain package; se mudar lá, atualizar aqui.
const LIMITES_MARGEM: Record<TipoMargem, number> = {
  EMPRESTIMO: 0.35,
  CARTAO_CONSIGNADO: 0.05,
  CARTAO_BENEFICIOS: 0.05,
};
const margemTotal = (salario: number, tipo: TipoMargem) => salario * LIMITES_MARGEM[tipo];
const margemDisponivel = (salario: number, comprometido: number, tipo: TipoMargem) =>
  Math.max(0, margemTotal(salario, tipo) - comprometido);
import {
  Button,
  ContratosTable,
  CpfRevelavel,
  CurrencyField,
  FormActions,
  FormGrid,
  MargemCalculadorBox,
  MargemProjecaoLinha,
  NumberField,
  SelectField,
  TextareaField,
  TextField,
  fmtBRL as fmtBRLUi,
} from "@atlas/ui/web";
import { CET_MINIMO_AM, avisoCoerenciaParcela, cetAnual } from "@atlas/domain";
import { atlas } from "../../../lib/sdk";
import { fmtBRL } from "../../../lib/banco-propostas";
import { formatBackendErr } from "../../../lib/format-backend-err";
import {
  bancoPodeAverbarPelaFicha,
  bancoPodeVerContratos,
  readBancoPermissoesDaSessao,
} from "../../../lib/banco-perms";

/** Mapeia tipoContrato do backend para o TipoMargem do domain. */
function tipoContratoParaMargem(tipoContrato: string): TipoMargem {
  const t = tipoContrato.toUpperCase();
  if (t === "ECONSIGNADO" || t === "CARTAO_CONSIGNADO") return "CARTAO_CONSIGNADO";
  if (t === "CARTAO_BENEFICIOS") return "CARTAO_BENEFICIOS";
  return "EMPRESTIMO"; // EMPRESTIMO, REFIN → margem de empréstimo
}

/** Contratos "vivos" que ainda consomem margem. Espelha EXATAMENTE o
 *  comprometeMargem de @atlas/domain (contrato.ts) — nao dá pra importar o domain
 *  no @atlas/web, entao replicamos; se mudar la, atualizar aqui. Regra: consome
 *  por padrao, exceto os estados terminais/recusados/falha/cobranca. Bug 30/07/2026:
 *  a versao antiga NAO reconhecia "Aprovado", entao um contrato Aprovado consumia
 *  margem no card (comprometeMargem no backend) mas SUMIA da tabela — a ficha
 *  mostrava "sem contratos ativos" com margem utilizada. Agora batem. */
function consumeMargem(situacao: string): boolean {
  const s = situacao.toLowerCase();
  if (s === "expirado" || s === "cancelado" || s === "quitado") return false;
  if (s.includes("recus") || s.includes("reprov") || s.includes("rejeit") || s.includes("negad") || s.includes("estorn")) return false;
  if (s.includes("falha em folha")) return false;
  if (s.includes("cobran")) return false;
  return true;
}

export function BancoMargemContratacaoFicha() {
  const { idMatricula = "" } = useParams<{ idMatricula: string }>();
  const [sp] = useSearchParams();
  // Convenio vem do seletor (index.tsx) quando o servidor tem a mesma matricula
  // em >1 prefeitura. Sem isso, o backend nao consegue distinguir qual carregar.
  const idConvenioParam = sp.get("convenio") ?? undefined;
  const nav = useNavigate();
  const qc = useQueryClient();
  const [modalAverbar, setModalAverbar] = useState<null | "aberto" | "sucesso">(null);
  const [sucessoAverbar, setSucessoAverbar] = useState<{ adf: string; exigeSenhaServidor: boolean; avisoAnexo?: string; reservaCartao?: boolean; reserva?: ReservaCartao } | null>(null);
  // Uma das cinco operacoes do "Tipo de margem" (ADR-0014). Empréstimo abre o
  // formulario de credito; as quatro de cartao, o de reserva e lancamento.
  const [operacao, setOperacao] = useState<OperacaoMargem>("EMPRESTIMO");
  // Com a reserva e lancamento de cartao em manutencao (producao), o card de
  // cartao abre o formulario de averbacao com o tipo do balde, como
  // antes da ADR-0014.
  const [tipoInicial, setTipoInicial] = useState<TipoMargem>("EMPRESTIMO");

  // Operador com `margem-consulta` (preset "Consulta de margem") le a ficha e
  // NAO averba. Quem barra e' a API (`/margem/:id/averbar` exige
  // `margem-contratacao`); aqui e' so' para nao oferecer o caminho que vai
  // responder 403. Pela mesma razao a lista de contratos, cujo portao e'
  // `carteira`/`propostas`, nem e' pedida — os cards de margem ja trazem o
  // utilizado calculado no backend e nao dependem dela.
  const permissoes = useMemo(() => readBancoPermissoesDaSessao(), []);
  const podeAverbar = bancoPodeAverbarPelaFicha(permissoes);
  const podeVerContratos = bancoPodeVerContratos(permissoes);

  // Buscou por CPF? A ficha busca pelo mesmo CPF — ver `cpf-buscado.ts`.
  const cpfBuscado = cpfBuscadoDo(useLocation().state);
  // A mesma chave na leitura e na atualizacao imediata pos-reserva.
  const fichaKey = ["banco", "margem", idMatricula, idConvenioParam ?? null, cpfBuscado ? "cpf" : "matricula"] as const;
  const ficha = useQuery({
    queryKey: fichaKey,
    queryFn: () => atlas.banco.margemBuscar(
      cpfBuscado
        ? { cpf: cpfBuscado, idMatricula, idConvenio: idConvenioParam }
        : { matricula: idMatricula.replace(/^MAT-/, ""), idConvenio: idConvenioParam },
    ),
    enabled: !!idMatricula,
  });

  const contratos = useQuery({
    queryKey: ["banco", "contratos", idMatricula],
    queryFn: () =>
      // SO os contratos gerados por ESTA matricula (idMatricula) no convenio
      // ativo. Nunca por CPF, nem de outra matricula do mesmo CPF (mesmo na
      // mesma prefeitura), nem de outra prefeitura. Cliente reportou 30/07/2026:
      // o banco logado em Guarulhos via os contratos do mesmo CPF em Capistrano.
      // O backend ja escopa com listContratos({ convenioId: ativo, matricula })
      // (matricula EXATA, store.ts:297) — basta NAO pedir incluir_todos_convenios,
      // que era justamente o que anexava os contratos das outras matriculas/pref.
      atlas.banco.contratos({ colaborador: idMatricula.replace(/^MAT-/, "") }),
    enabled: !!idMatricula && podeVerContratos,
  });

  const hoje = new Date();
  const [mes, setMes] = useState<string>(monthLabel(hoje.getMonth() + 1));
  const [ano, setAno] = useState<number>(hoje.getFullYear());
  const calcular = useMutation({
    mutationFn: () => atlas.banco.margemCalcular(idMatricula, { mes: monthIndex(mes), ano }),
  });

  // Comprometido por tipo de margem, com base nos contratos vivos do servidor.
  const comprometidoPorTipo = useMemo(() => {
    const acc: Record<TipoMargem, number> = { EMPRESTIMO: 0, CARTAO_CONSIGNADO: 0, CARTAO_BENEFICIOS: 0 };
    for (const c of contratos.data?.contratos ?? []) {
      if (!consumeMargem(c.situacao)) continue;
      acc[tipoContratoParaMargem(c.tipoContrato)] += c.valorParcela;
    }
    return acc;
  }, [contratos.data]);

  // Fechou o aviso de sucesso. RESERVA DE CARTAO fica na ficha (14/10/2026): o
  // passo seguinte — o lancamento — e' nesta mesma ficha, e ela recarrega para
  // mostrar o balde zerado e o card "Lançar →". O resto segue o pedido do
  // cliente de 06/08/2026: volta pro inicio de Margem / Contratacao (a busca por
  // CPF ou matricula), pronto pra proxima operacao.
  function fecharSucesso() {
    if (sucessoAverbar?.reservaCartao && ficha.data?.ficha) {
      setModalAverbar(null);
      setSucessoAverbar(null);
      qc.invalidateQueries({ queryKey: ["banco", "margem", idMatricula] });
      return;
    }
    nav("/banco/margem-contratacao");
  }

  if (ficha.isLoading) return <div style={{ color: "var(--text-muted)" }}>Carregando ficha...</div>;
  // Erro so' substitui a pagina quando nao ha ficha nenhuma. Uma RECARGA que
  // falha (a da reserva acima) mantem a ficha que ja' estava na tela.
  if (!ficha.data) {
    // A averbacao JA foi gravada quando chegamos aqui com sucesso pendente. O
    // operador nao pode perder a confirmacao (e o numero da ADF) so porque a
    // ficha nao recarregou — foi exatamente isso que apagou o modal de sucesso
    // na tela do cliente em 06/08/2026: a ficha de um servidor de OUTRO convenio
    // que o ativo devolve 404, e o erro substituia a pagina inteira.
    if (modalAverbar === "sucesso" && sucessoAverbar) {
      return (
        <AverbarSucessoModal
          adf={sucessoAverbar.adf}
          exigeSenhaServidor={sucessoAverbar.exigeSenhaServidor}
          avisoAnexo={sucessoAverbar.avisoAnexo}
          reservaCartao={sucessoAverbar.reservaCartao}
          onClose={fecharSucesso}
        />
      );
    }
    return <div style={{ color: "var(--danger-500)" }}>Erro ao carregar servidor.</div>;
  }
  // ficha vira opcional pos-29/07/2026 (backend retorna `matriculas` quando >1),
  // mas nesta rota so cai aqui apos idMatricula ser fixado — sempre retorna ficha.
  if (!ficha.data.ficha) return <div style={{ color: "var(--danger-500)" }}>Ficha nao carregada.</div>;

  const f = ficha.data.ficha;
  // LGPD: banco NAO recebe salarioLiquido — usa `margens` pré-calculadas
  // pelo backend (contem total/utilizado/disponivel por bucket).
  // Fallback pro calculo local so quando o backend nao trouxer (SDK antigo).
  const labelOf = (t: TipoMargem): string =>
    t === "EMPRESTIMO"
      ? "Empréstimo Consignado"
      : t === "CARTAO_CONSIGNADO"
        ? "Cartão de Crédito Consignado"
        : "Cartão Benefício Consignado";
  const margens: {
    tipo: TipoMargem;
    label: string;
    total: number;
    utilizado: number;
    disponivel: number;
    saldo: number | null;
  }[] =
    f.margens && f.margens.length > 0
      // Fracao de centavo vira zero: a API recusa operar nela, e o card
      // desabilita junto (15/10/2026). API anterior ao `saldo` (#352): sem ele,
      // o card nao sabe se a margem foi excedida e fica como era.
      ? f.margens.map((m) => ({
          ...m,
          disponivel: disponivelUtil(m.disponivel),
          saldo: m.saldo ?? null,
          label: labelOf(m.tipo),
        }))
      : (["EMPRESTIMO", "CARTAO_CONSIGNADO", "CARTAO_BENEFICIOS"] as const).map((t) => {
          const salario = 0; // sem backend, nao ha como calcular — retorna zero
          const utilizado = comprometidoPorTipo[t];
          return {
            tipo: t,
            label: labelOf(t),
            total: margemTotal(salario, t),
            utilizado,
            disponivel: margemDisponivel(salario, utilizado, t),
            saldo: null,
          };
        });

  // A API decide por ambiente se a reserva e lancamento de cartao estao no ar:
  // sim em homologacao, nao em producao (_shared/cartao-manutencao.ts na API).
  // A tela so' obedece — chave propria aqui e' como tela e rotas se desencontram.
  // Backend anterior nao manda o campo: fica em manutencao.
  const cartaoDisponivel = f.cartaoReservaLancamentoDisponivel === true;
  // Reservas de cartao DESTE banco no vinculo (ADR-0014), e as competencias que
  // ainda aceitam lancamento. Em manutencao, nenhuma das duas: a tela fica como
  // antes da ADR-0014.
  const reservas = cartaoDisponivel ? (f.reservasCartao ?? []) : [];
  const competenciasLancamento = cartaoDisponivel ? (f.competenciasLancamento ?? []) : [];

  // Contratos "ativos do servidor" para a tabela do modelo (apenas os que ainda vivem).
  const contratosAtivos = (contratos.data?.contratos ?? []).filter((c) => consumeMargem(c.situacao));
  const situacaoFuncional = (f.situacaoFuncional || "").toLowerCase();
  const isAtivo = situacaoFuncional === "ativo" || situacaoFuncional === "trabalhando";

  const calc = calcular.data ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 1200 }}>
      {/* Header no modelo: nome grande + subtítulo (mat, CPF com olho, situação/origem) + pills à direita */}
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Margem / Contratação
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>{f.nome}</h1>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
            Mat: <span style={{ fontFamily: "var(--font-mono)" }}>{f.matricula}</span>
            {" · "}CPF:{" "}
            <CpfRevelavel
              cpf={f.cpf}
              cpfMasked={f.cpfMasked}
              motivoSemCpf="CPF completo só para clientes da consignatária ou quando a busca é feita pelo CPF."
            />
            {f.origem ? <> · {f.origem}</> : null}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* A recarga da ficha e' lenta (8–16 s em homologacao): sem isto a tela
              parecia parada depois da reserva. */}
          {ficha.isFetching ? (
            <span role="status" style={{ fontSize: 12, color: "var(--text-muted)" }}>Atualizando ficha…</span>
          ) : null}
          <StatusPill on={isAtivo}>{isAtivo ? "● Ativo" : f.situacaoFuncional || "Situação"}</StatusPill>
          <StatusPill neutro>{f.vinculo || "Efetivo"}</StatusPill>
          <button
            type="button"
            onClick={() => nav("/banco/margem-contratacao")}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--accent)",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ← Nova busca
          </button>
        </div>
      </header>

      {f.vinculoMargem ? <AvisoVinculoMargem v={f.vinculoMargem} /> : null}

      {/* 3 cards de margem lado a lado — modelo da screenshot */}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
        {margens.map((m) => (
          <MargemTipoCard
            key={m.tipo}
            label={m.label}
            total={m.total}
            utilizado={m.utilizado}
            disponivel={m.disponivel}
            saldo={m.saldo}
          />
        ))}
      </section>
      {/* DÍVIDA COM CREDOR A IDENTIFICAR (ADR-0010): abate a margem como qualquer
          obrigação legada, mas este banco não a vê na carteira. Sem esta linha o
          banco veria a margem menor sem saber por quê — e a decisão de 10/09/2026
          foi mostrar a contagem, nunca de quem é a dívida. */}
      {(f.dividasCredorAIdentificar ?? 0) > 0 ? (
        <p style={{ margin: "-4px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
          A margem acima já desconta <strong>{f.dividasCredorAIdentificar}</strong> dívida(s) em folha cujo
          credor ainda não está cadastrado na Atlas. Elas existem e são descontadas todo mês; o que
          falta é o credor assinar convênio.
        </p>
      ) : null}

      {!podeAverbar ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
          Acesso somente consulta — a averbação está desabilitada para este operador.
        </p>
      ) : null}

      {/* As cinco operacoes no MESMO formulario (05/10/2026): emprestimo,
          reserva e lancamento de cartao. `key` refaz o estado ao trocar de
          operacao — os campos de uma nao valem para a outra. */}
      {podeAverbar && modalAverbar === "aberto" ? (
        <AverbarMargemModal
          key={operacao}
          cartaoDisponivel={cartaoDisponivel}
          operacao={operacao}
          competencias={competenciasLancamento}
          idMatricula={idMatricula}
          idConvenio={idConvenioParam}
          nomeServidor={f.nome}
          margens={margens}
          reservas={reservas}
          onTrocarOperacao={setOperacao}
          tipoInicial={tipoInicial}
          exigeSenhaServidor={f.exigeSenhaAverbacao}
          limitesConvenio={f.limitesConvenio}
          onClose={() => setModalAverbar(null)}
          onSuccess={(r) => {
            setSucessoAverbar(r);
            setModalAverbar("sucesso");
            // RESERVA: a ficha ja' fica certa por baixo do aviso — balde zerado e
            // card "Lançar →" — com a reserva que a propria resposta trouxe. A
            // recarga completa (8–16 s em homologacao) vem ao fechar o aviso.
            if (r.reserva) {
              const reserva = r.reserva;
              qc.setQueryData(fichaKey, (old: typeof ficha.data) =>
                old?.ficha ? { ...old, ficha: aplicarReservaNaFicha(old.ficha, reserva) } : old,
              );
            }
            // Rele o saldo consumido. O tratamento de erro acima preserva o
            // comprovante de sucesso mesmo se a ficha nao puder ser relida.
            qc.invalidateQueries({ queryKey: ["banco", "margem", idMatricula, idConvenioParam ?? null] });
            qc.invalidateQueries({ queryKey: ["banco", "contratos"] });
          }}
        />
      ) : null}
      {modalAverbar === "sucesso" && sucessoAverbar ? (
        <AverbarSucessoModal
          adf={sucessoAverbar.adf}
          exigeSenhaServidor={sucessoAverbar.exigeSenhaServidor}
          avisoAnexo={sucessoAverbar.avisoAnexo}
          reservaCartao={sucessoAverbar.reservaCartao}
          onClose={fecharSucesso}
        />
      ) : null}

      {/* Contratos ativos do servidor — tabela do modelo */}
      {podeVerContratos ? (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Contratos ativos do servidor
          </div>
          <ContratosTable
            loading={contratos.isLoading}
            rows={contratosAtivos.map((c) => ({
              adf: c.adf,
              situacao: c.situacao,
              lancamento: c.lancamento,
              // Reserva mostra a validade (+48h); contrato, a ultima parcela.
              // Regra em `expiracaoDoContrato`.
              expiracao: expiracaoDoContrato(c),
              // Reserva de cartao (ADR-0014): o valor da coluna e' o RESERVADO,
              // nao uma parcela — o tipo diz isso na propria linha.
              tipoContrato: reservas.some((r) => r.adf === c.adf) ? "Reserva cartão (valor reservado)" : c.tipoContrato,
              totalParcelas: c.totalParcelas,
              valorParcela: c.valorParcela,
              convenio: c.convenio,
            }))}
            emptyState="Este servidor não possui contratos ativos."
          />
        </section>
      ) : null}

      {/* Averbacao INICIADA PELO BANCO — bloco grande, entre contratos ativos
          e a simulacao. Cada bucket abre o modal com o tipo pre-selecionado.
          Cliente pediu 30/07/2026 o retorno do layout maior. */}
      {podeAverbar ? (
        <section
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
                Averbação iniciada pela consignatária
              </div>
              <h2 style={{ margin: "4px 0 0", fontSize: 18 }}>Averbar margem do servidor</h2>
              <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: 13, lineHeight: 1.5, maxWidth: 640 }}>
                {cartaoDisponivel ? (
                  <>
                    No empréstimo, informe as condições do contrato como calculadas no seu sistema — a
                    Atlas confere margem, teto de CET e prazo. No cartão, a <b>reserva</b> trava a margem e não vai
                    para a folha; o <b>lançamento</b> é o valor que a folha desconta em cada competência.
                  </>
                ) : (
                  <>
                    Escolha o bucket de margem que vai ser consumido e informe as condições do contrato
                    como calculadas no seu sistema — a Atlas confere margem, teto de CET e prazo.
                  </>
                )}
                {f.exigeSenhaAverbacao ? " O órgão exige a senha do servidor pra concluir." : ""}
              </p>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            {margens.map((m) => {
              // Em manutencao, todo balde se comporta como o do empréstimo: abre o
              // formulario de averbacao com o tipo dele.
              if (!cartaoDisponivel) {
                return (
                  <AverbarBucketCard
                    key={m.tipo}
                    label={m.label}
                    disponivel={m.disponivel}
                    disabled={m.disponivel <= 0}
                    cta={rotuloCtaBucketAverbacao(m.disponivel)}
                    acao="Averbar →"
                    onClick={() => {
                      setOperacao("EMPRESTIMO");
                      setTipoInicial(m.tipo);
                      setSucessoAverbar(null);
                      setModalAverbar("aberto");
                    }}
                  />
                );
              }
              const reserva = reservaAtivaDoBalde(reservas, m.tipo);
              const ultimo = reserva?.lancamentos.filter((l) => l.estado === "informado").at(-1);
              const emprestimo = m.tipo === "EMPRESTIMO";
              return (
                <AverbarBucketCard
                  key={m.tipo}
                  label={m.label}
                  disponivel={m.disponivel}
                  // Emprestimo e reserva de cartao precisam de margem; o lancamento, de
                  // uma reserva — mesmo com o balde zerado, porque a reserva e'
                  // justamente o que o ocupou.
                  disabled={emprestimo ? m.disponivel <= 0 : !reserva && m.disponivel <= 0}
                  cta={emprestimo ? rotuloCtaBucketAverbacao(m.disponivel) : rotuloCtaBucketCartao(m.disponivel, !!reserva)}
                  acao={emprestimo ? "Averbar →" : reserva ? "Lançar →" : "Reservar →"}
                  detalhe={
                    reserva
                      ? `Reservado por você: ${fmtBRL(reserva.valorReservado)} · parcela ${fmtBRL(reserva.valorParcela)}${ultimo ? ` · lançamento ${rotuloCompetencia(ultimo.competencia)}: ${fmtBRL(ultimo.valor)}` : ""}`
                      : undefined
                  }
                  onClick={() => {
                    setOperacao(operacaoDoCard(m.tipo, reservas));
                    setSucessoAverbar(null);
                    setModalAverbar("aberto");
                  }}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Simulação avançada — mantida como opcional pra recalcular por competência */}
      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: "var(--text-muted)" }}>Simulação por competência</h2>
        <MargemCalculadorBox
          mes={mes}
          ano={ano}
          onMesChange={setMes}
          onAnoChange={setAno}
          total={calc?.total ?? null}
          disponivel={calc?.disponivel ?? null}
          tipo="EMPRESTIMO"
          calculando={calcular.isPending}
          onCalcular={() => calcular.mutate()}
        />
        {calc?.impedimento ? (
          // Mesma regra da averbacao: o banco sabe por que nao pode contratar
          // antes de tentar, e nao so' depois do 422.
          <div role="alert" style={{ border: "1px solid var(--danger-500)", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
            {calc.impedimento.mensagem}
          </div>
        ) : null}
        {calc ? <MargemProjecaoLinha meses={calc.projecao} /> : null}
      </section>
    </div>
  );
}

/**
 * Card do modelo: título + pill de estado + progress bar + TOTAL/UTILIZADO/DISPONÍVEL.
 *
 * Com o `saldo` negativo a margem está EXCEDIDA (#352): o card diz quanto
 * passou, em vermelho. Antes mostrava "R$ 0,00" verde com o selo "Disponível",
 * e o banco desaverbou sem saber que a margem do servidor estava negativa.
 */
export function MargemTipoCard({
  label,
  total,
  utilizado,
  disponivel,
  saldo,
}: {
  label: string;
  total: number;
  utilizado: number;
  disponivel: number;
  /** Disponível sem a trava em zero; `null` quando o zero vem de impedimento. */
  saldo: number | null;
}) {
  const excedida = saldo != null && saldo < 0;
  const semMargem = !excedida && disponivel <= 0;
  const pct = excedida ? 1 : total > 0 ? Math.min(1, utilizado / total) : 0;
  const semUso = utilizado === 0 && total > 0;
  const pill = excedida
    ? { texto: "● Excedida", cor: "var(--danger-500)" }
    : semMargem
      ? { texto: "● Sem margem", cor: "var(--text-muted)" }
      : { texto: "● Disponível", cor: "var(--emerald-500)" };
  return (
    <article
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: 18,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{label}</div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: pill.cor,
            border: `1px solid ${pill.cor}`,
            borderRadius: 999,
            padding: "2px 10px",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {pill.texto}
        </span>
      </div>
      {/* Progress bar mostrando utilizado / total; excedida, cheia e vermelha */}
      <div
        style={{ height: 6, borderRadius: 6, background: "var(--bg-elev-2)", overflow: "hidden", marginBottom: 14 }}
        title={excedida ? "Acima de 100% da margem" : undefined}
      >
        <div
          style={{
            width: `${Math.round(pct * 100)}%`,
            height: "100%",
            background: excedida ? "var(--danger-500)" : semUso ? "var(--emerald-500)" : "var(--gold-500)",
          }}
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        <ValorLinha label="TOTAL" value={fmtBRL(total)} color="var(--text)" />
        <ValorLinha label="UTILIZADO" value={fmtBRL(utilizado)} color={utilizado > 0 ? "var(--gold-500)" : "var(--text-muted)"} />
        {excedida ? (
          <ValorLinha label="EXCEDIDA" value={fmtBRL(-saldo)} color="var(--danger-500)" bold />
        ) : (
          <ValorLinha
            label="DISPONÍVEL"
            value={fmtBRL(disponivel)}
            color={semMargem ? "var(--text-muted)" : "var(--emerald-500)"}
            bold
          />
        )}
      </div>
      {excedida ? (
        <div role="alert" style={{ marginTop: 12, fontSize: 12, fontWeight: 700, color: "var(--danger-500)" }}>
          Margem excedida em {fmtBRL(-saldo)}: as parcelas passam de 100% da margem.
        </div>
      ) : null}
    </article>
  );
}

function ValorLinha({ label, value, color, bold }: { label: string; value: string; color: string; bold?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: bold ? 800 : 700, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

type VinculoMargem = NonNullable<NonNullable<Awaited<ReturnType<typeof atlas.banco.margemBuscar>>["ficha"]>["vinculoMargem"]>;

/**
 * O vínculo e o limite de parcelas que a configuração da margem do órgão
 * declara, ANTES de o banco montar a proposta. Sem isto o limite da categoria
 * (ex.: 24 meses do comissionado) só aparecia no 422 da averbação.
 */
export function AvisoVinculoMargem({ v }: { v: VinculoMargem }) {
  const teto = `${v.tetoParcelas} parcelas`;
  let vinculo: React.ReactNode;
  let limite: React.ReactNode;
  if (v.folha == null) {
    vinculo = "o órgão não informou margem deste vínculo no último arquivo";
    limite = <>teto do órgão de <strong>{teto}</strong>; a contratação é recusada até o próximo arquivo.</>;
  } else if (v.categoria == null) {
    vinculo = <>folha <strong>{v.folha}</strong>, sem categoria cadastrada</>;
    limite = <>nova consignação não é permitida até o cadastro da categoria (teto do órgão: {teto}).</>;
  } else if (v.elegivel === false) {
    vinculo = <>folha <strong>{v.folha}</strong> · <strong>{v.categoria}</strong></>;
    limite = <>{v.mensagemRecusa ?? "esta categoria não é elegível para nova consignação."}</>;
  } else {
    vinculo = <>folha <strong>{v.folha}</strong> · <strong>{v.categoria}</strong></>;
    limite = v.prazoMaxMeses != null
      ? <>até <strong>{v.prazoMaxMeses} meses</strong> entre a contratação e a última parcela, carência incluída (teto do órgão: {teto}).</>
      : <>até <strong>{teto}</strong> (teto do órgão).</>;
  }
  return (
    <div
      role="note"
      style={{
        border: "1px solid var(--border)",
        borderLeft: "3px solid var(--accent)",
        borderRadius: "var(--radius-md)",
        background: "var(--bg-elev)",
        padding: "10px 14px",
        fontSize: 13,
        color: "var(--text-muted)",
        display: "grid",
        gap: 2,
      }}
    >
      <span>Vínculo na configuração da margem: {vinculo}.</span>
      <span>Limite de parcelas: {limite}</span>
    </div>
  );
}

function StatusPill({ on, neutro, children }: { on?: boolean; neutro?: boolean; children: React.ReactNode }) {
  const cor = neutro ? "var(--text-muted)" : on ? "var(--emerald-500)" : "var(--gold-500)";
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: cor,
        border: `1px solid ${cor}`,
        borderRadius: 999,
        padding: "3px 10px",
      }}
    >
      {children}
    </span>
  );
}

/** Card grande de um bucket de margem no bloco "Averbar operação". Clique
 *  abre o modal de averbação com o tipo desse bucket pre-selecionado. */
function AverbarBucketCard({
  label,
  disponivel,
  disabled,
  cta,
  acao,
  detalhe,
  onClick,
}: {
  label: string;
  disponivel: number;
  disabled: boolean;
  cta: string;
  /** Texto do botao: "Averbar →", "Reservar →" ou "Lançar →". */
  acao: string;
  /** A reserva deste banco no cartao, quando ha'. */
  detalhe?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: "left",
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        color: "var(--text)",
        font: "inherit",
        transition: "border-color .15s, transform .15s",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = "var(--gold-500)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{label}</div>
      <div>
        <div style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Disponível
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: disponivel > 0 ? "var(--emerald-500)" : "var(--text-muted)", marginTop: 2 }}>
          {fmtBRL(disponivel)}
        </div>
      </div>
      {detalhe ? <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{detalhe}</div> : null}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {cta}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: disabled ? "var(--text-muted)" : "var(--gold-500)" }}>
          {disabled ? "—" : acao}
        </span>
      </div>
    </button>
  );
}

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
function monthLabel(m: number): string { return MESES[m - 1] ?? "Janeiro"; }
function monthIndex(label: string): number { return MESES.indexOf(label) + 1 || 1; }

/** Modal "Averbar margem" — feature 4+6 (cliente 29/07/2026). Escolhe bucket,
 *  valor, parcelas, taxa. Se a prefeitura exigir senha do servidor, contrato
 *  vira "Aguardando Confirmacao do Servidor"; senao, entra Ativo direto. */
function AverbarMargemModal({
  idMatricula,
  idConvenio,
  nomeServidor,
  margens,
  reservas,
  cartaoDisponivel,
  operacao,
  competencias,
  onTrocarOperacao,
  tipoInicial,
  exigeSenhaServidor,
  limitesConvenio,
  onClose,
  onSuccess,
}: {
  /** Reserva e lancamento de cartao no ar neste ambiente (vem da ficha). Fora
   *  do ar, o formulario e' o dos tres baldes, sempre no modo emprestimo. */
  cartaoDisponivel: boolean;
  /** Uma das cinco operacoes do "Tipo de margem" — decide o modo do formulario. */
  operacao: OperacaoMargem;
  /** Competencias que ainda aceitam lancamento, com o corte. */
  competencias: readonly CompetenciaLancamento[];
  idMatricula: string;
  /** Convenio DO BANCO sob o qual averbar, quando ele tem mais de um nesta
   *  prefeitura. Vem do `?convenio=` da URL. Nao e' convenio do servidor — ele
   *  nao tem (ADR 0002). Ausente: o backend resolve se houver um so, e recusa
   *  com mensagem explicita se houver ambiguidade. */
  idConvenio: string | undefined;
  nomeServidor: string;
  margens: { tipo: TipoMargem; label: string; total: number; utilizado: number; disponivel: number }[];
  /** Reservas de cartao do banco no vinculo — o select mostra o valor delas. */
  reservas: NonNullable<Parameters<typeof SelectOperacaoMargem>[0]["reservas"]>;
  /** O select "Tipo de margem" trocou para uma operacao de cartao. */
  onTrocarOperacao: (op: OperacaoMargem) => void;
  /** Balde do card clicado. So' vale com a reserva e lancamento de cartao em
   *  manutencao: ai' o formulario averba qualquer balde, como antes da ADR-0014. */
  tipoInicial: TipoMargem;
  /** Vem da prefeitura do servidor via margemBuscar. Quando true, o modal
   *  mostra o campo de senha e o backend valida antes de averbar. */
  exigeSenhaServidor: boolean;
  /** Tetos da PREFEITURA pro convenio. O banco escolhe taxa e prazo dentro
   *  deles. taxaMaxAm em PERCENTUAL (6 = 6% a.m.); null = sem teto.
   *  prazoMin/prazoMax: faixa das tabelas do banco limitada pelo teto. */
  limitesConvenio?: { taxaMaxAm: number | null; maxParcelas: number; prazoMin?: number; prazoMax?: number };
  onClose: () => void;
  onSuccess: (r: { adf: string; exigeSenhaServidor: boolean; avisoAnexo?: string; reservaCartao?: boolean; reserva?: ReservaCartao }) => void;
}) {
  // Condicoes INFORMADAS pelo banco (30/09/2026). O banco tem a propria regra de
  // calculo: parcela, financiado, liquido, IOF, CET e numero do contrato saem do
  // sistema dele e sao digitados aqui. Nada e' pre-preenchido por conta — a
  // Atlas confere so' os limites (margem, teto de CET do orgao, faixa de prazo)
  // e AVISA quando a parcela nao condiz com o CET. Antes esta tela calculava a
  // parcela pelo CET (spec 014, decisao 4 — revertida) e emitia o numero.
  // TRES MODOS, UM FORMULARIO (05/10/2026). O banco digita as mesmas condicoes
  // do contrato no emprestimo, na reserva de cartao e em cada lancamento:
  //  - reserva: `parcelas` e' o PRAZO e `valor da parcela` o reservado por mes;
  //  - lancamento: a parcela do MES da reserva — parcelas travadas em 1, e a
  //    tela diz qual parcela e' e quantas faltam.
  // Em manutencao (`cartaoDisponivel` falso, producao) o formulario volta a ser
  // o dos tres baldes, com o tipo escolhido no select, e o modo e' sempre o do
  // emprestimo.
  const [tipoEscolhido, setTipo] = useState<TipoMargem>(tipoInicial);
  const tipo: TipoMargem = cartaoDisponivel ? baldeDaOperacao(operacao) : tipoEscolhido;
  const modo: "emprestimo" | "reserva" | "lancamento" =
    !cartaoDisponivel || operacao === "EMPRESTIMO" ? "emprestimo" : operacaoEhLancamento(operacao) ? "lancamento" : "reserva";
  const qc = useQueryClient();
  const reservaDaFicha = modo === "lancamento" ? reservaAtivaDoBalde(reservas, tipo) : undefined;
  // A reserva relida a cada lancamento: e' dela que vem a parcela vigente e o
  // historico. Recarregar a FICHA pode responder 404 (servidor de outro
  // convenio que o ativo, 06/08/2026) e levar o modal junto.
  const detalheReserva = useQuery({
    queryKey: ["banco", "cartao", "reserva", reservaDaFicha?.adf ?? ""],
    queryFn: () => atlas.banco.lancamentosDaReserva(reservaDaFicha!.adf),
    enabled: !!reservaDaFicha,
  });
  const reserva = detalheReserva.data?.reserva ?? reservaDaFicha;
  const abertas = competencias.filter((c) => !reserva || c.competencia >= reserva.competenciaInicial);
  const [competencia, setCompetencia] = useState<string>(abertas[0]?.competencia ?? "");
  const [avisoOk, setAvisoOk] = useState<string | null>(null);
  const [numeroContratoBanco, setNumeroContratoBanco] = useState<string>(reservaDaFicha?.numeroContratoBanco ?? "");
  const [vencimento1aParcela, setVencimento1aParcela] = useState<string>("");
  const [dataLiberacaoCredito, setDataLiberacaoCredito] = useState<string>("");
  const [valor, setValor] = useState<number | null>(null);
  const [valorLiquido, setValorLiquido] = useState<number | null>(null);
  const [valorParcela, setValorParcela] = useState<number | null>(null);
  const [iof, setIof] = useState<number | null>(null);
  const [valorCarencia, setValorCarencia] = useState<number | null>(null);
  // Numeros digitados como texto: campo vazio e' "nao informado", nao zero.
  const [parcelasTxt, setParcelasTxt] = useState<string>("");
  const [cetTxt, setCetTxt] = useState<string>("");
  const [jurosTxt, setJurosTxt] = useState<string>("");
  const [diasCarenciaTxt, setDiasCarenciaTxt] = useState<string>("");
  const [observacoes, setObservacoes] = useState<string>("");
  const [senhaServidor, setSenhaServidor] = useState<string>("");
  const [erro, setErro] = useState<string | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erroArquivo, setErroArquivo] = useState<string | null>(null);

  const num = (s: string): number | null => {
    const t = s.trim().replace(",", ".");
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };
  // No lancamento a quantidade e' sempre 1: e' a parcela do mes.
  const parcelas = modo === "lancamento" ? 1 : num(parcelasTxt);
  const cetPct = num(cetTxt);
  const jurosPct = num(jurosTxt);
  const diasCarenciaInformado = num(diasCarenciaTxt);
  const diasCarencia = diasCarenciaInformado ?? 0;
  const cetAm = cetPct != null ? Number((cetPct / 100).toFixed(6)) : null;
  const jurosAm = jurosPct != null && jurosPct > 0 ? Number((jurosPct / 100).toFixed(6)) : null;
  /** Dias entre a liberacao e a 1a parcela — so' dica ao lado do campo, nunca
   *  preenche: quem informa a carencia e' o banco. */
  const diasEntreDatas = useMemo(() => {
    if (!dataLiberacaoCredito || !vencimento1aParcela) return null;
    const d = (Date.parse(vencimento1aParcela) - Date.parse(dataLiberacaoCredito)) / 86_400_000;
    return Number.isFinite(d) && d >= 0 ? Math.round(d) : null;
  }, [dataLiberacaoCredito, vencimento1aParcela]);

  /** Spec 014: em emprestimo a taxa e' o CET, com teto; cartao informa juros e CET. */
  const ehEmprestimo = tipo === "EMPRESTIMO";
  const margemAtual = margens.find((m) => m.tipo === tipo);
  // A faixa das tabelas e' de emprestimo; o cartao respeita so' o teto do orgao.
  const tetoOrgao = Math.min(limitesConvenio?.maxParcelas ?? 120, 120);
  const prazoMin = ehEmprestimo ? (limitesConvenio?.prazoMin ?? 1) : 1;
  const prazoMax = ehEmprestimo ? (limitesConvenio?.prazoMax ?? tetoOrgao) : tetoOrgao;
  /** Teto de CET do orgao, em %. So' vale para emprestimo. */
  const tetoCetPct = ehEmprestimo ? (limitesConvenio?.taxaMaxAm ?? null) : null;

  const folha1oDesconto = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(vencimento1aParcela);
    return m ? `${m[2]}/${m[1]}` : "";
  }, [vencimento1aParcela]);
  const valorTotalPagar = valorParcela != null && parcelas != null ? Math.round(valorParcela * parcelas * 100) / 100 : 0;

  // Travas — as mesmas que o backend aplica. No lancamento o teto e' a PARCELA
  // contratada na reserva: o reservado e' o balde inteiro (14/10/2026), e a
  // margem ja' esta' ocupada por ele.
  const tetoParcela = modo === "lancamento" ? (reserva?.valorParcela ?? 0) : (margemAtual?.disponivel ?? null);
  // No cartao a tolerancia de um centavo vale so' quando ha margem — a mesma
  // regra da API: num balde zerado, nem R$ 0,01 cabe (15/10/2026).
  const excedeMargem = tetoParcela != null && valorParcela != null && (
    modo !== "lancamento" && !ehEmprestimo && tetoParcela < MARGEM_MINIMA
      ? valorParcela > 0
      : valorParcela > tetoParcela + (modo === "lancamento" ? 0.005 : 0.01)
  );
  const parcelasEsgotadas = modo === "lancamento" && reserva != null && reserva.parcelasTotal > 0 && reserva.parcelaVigente == null;
  const existente = modo === "lancamento"
    ? reserva?.lancamentos.find((l) => l.competencia === competencia && l.estado === "informado")
    : undefined;
  // Reenviar uma competencia ja' lancada substitui — nao gasta parcela nova.
  const parcelaDoForm = existente?.parcelaNumero ?? reserva?.parcelaVigente ?? null;
  const excedeCet = tetoCetPct != null && cetPct != null && cetPct > tetoCetPct + 1e-9;
  const cetAbaixoMinimo = ehEmprestimo && cetAm != null && cetAm > 0 && cetAm < CET_MINIMO_AM - 1e-9;
  // O CET inclui os juros: abaixo deles e' quase sempre os juros da tela do
  // banco digitados no campo de CET.
  const cetAbaixoJuros = ehEmprestimo && jurosPct != null && jurosPct > 0 && cetPct != null && cetPct < jurosPct - 1e-9;
  const prazoFora = modo !== "lancamento" && parcelas != null && (parcelas < prazoMin || parcelas > prazoMax || !Number.isInteger(parcelas));
  const liquidoAcimaDoFinanciado = valor != null && valorLiquido != null && valorLiquido > valor + 0.005;

  // Aviso — nao trava: a parcela digitada nao condiz com o CET digitado.
  const avisoCoerencia = ehEmprestimo && valorParcela != null && parcelas != null && cetAm != null
    ? avisoCoerenciaParcela({ parcela: valorParcela, valorLiquido: valorLiquido ?? valor ?? 0, parcelas, cetAm, diasCarencia })
    : null;

  const faltaObrigatorio =
    !numeroContratoBanco.trim() ||
    !vencimento1aParcela ||
    !dataLiberacaoCredito ||
    valor == null || valor <= 0 ||
    valorLiquido == null || valorLiquido <= 0 ||
    valorParcela == null || valorParcela <= 0 ||
    parcelas == null || parcelas <= 0 ||
    cetAm == null || cetAm <= 0 ||
    iof == null ||
    diasCarenciaInformado == null || diasCarenciaInformado < 0 ||
    (!ehEmprestimo && (jurosPct == null || jurosPct <= 0)) ||
    (modo === "lancamento" && (!competencia || !reserva));

  const mut = useMutation({
    mutationFn: async (): Promise<
      | { tipo: "contrato"; contrato: { adf: string; exigeSenhaServidor: boolean }; avisoAnexo?: string; reserva?: ReservaCartao }
      | { tipo: "lancamento"; acao: string }
    > => {
      if (valor == null || valorParcela == null || parcelas == null || cetAm == null || iof == null) {
        throw new Error("Preencha os campos obrigatórios.");
      }
      const jurosFracao = Number(((jurosPct ?? 0) / 100).toFixed(6));
      if (modo === "lancamento") {
        if (!reserva) throw new Error("Sem reserva ativa neste cartão.");
        const r = await atlas.banco.informarLancamentoCartao(reserva.adf, {
          competencia,
          valor: valorParcela,
          numeroContratoBanco: numeroContratoBanco.trim(),
          vencimento: vencimento1aParcela,
          dataLiberacao: dataLiberacaoCredito,
          valorFinanciado: valor,
          valorLiquido: valorLiquido ?? valor,
          iof,
          jurosAm: jurosFracao,
          cetAm,
          diasCarencia,
          valorCarencia: valorCarencia ?? undefined,
          observacoes: observacoes.trim() || undefined,
        });
        return { tipo: "lancamento", acao: r.acao };
      }
      if (modo === "reserva") {
        const r = await atlas.banco.reservarCartao(idMatricula, {
          produto: tipo as ProdutoCartao,
          valorParcela,
          parcelas,
          valor,
          valorLiquidoLiberado: valorLiquido ?? valor,
          jurosAm: jurosFracao,
          cetAm,
          iof,
          diasCarencia,
          valorCarencia: valorCarencia ?? undefined,
          numeroContratoBanco: numeroContratoBanco.trim(),
          vencimento1aParcela,
          dataLiberacaoCredito,
          idConvenio,
          senhaServidor: exigeSenhaServidor ? senhaServidor : undefined,
          observacoes: observacoes.trim() || undefined,
        });
        let avisoAnexo: string | undefined;
        if (arquivo) {
          try {
            await atlas.banco.uploadCcb(r.contrato.adf, arquivo);
          } catch (e) {
            avisoAnexo = `O contrato assinado NAO foi anexado (${(e as Error).message}). A reserva esta valida — reenvie o arquivo em Propostas › ${r.contrato.adf}.`;
          }
        }
        return { tipo: "contrato", contrato: r.contrato, avisoAnexo, reserva: r.reserva };
      }
      // Emprestimo: `taxaAm` e' o CET. Cartao: `taxaAm` sao os juros e o CET vai a parte.
      const taxaAm = ehEmprestimo ? cetAm : Number(((jurosPct ?? 0) / 100).toFixed(6));
      const r = await atlas.banco.averbarDireto(idMatricula, {
        tipo,
        valor,
        parcelas,
        taxaAm,
        jurosAm: ehEmprestimo ? (jurosAm ?? undefined) : undefined,
        valorParcela,
        iof,
        cetAm: ehEmprestimo ? undefined : cetAm,
        diasCarencia,
        observacoes: observacoes.trim() || undefined,
        idConvenio,
        senhaServidor: exigeSenhaServidor ? senhaServidor : undefined,
        numeroContratoBanco: numeroContratoBanco.trim(),
        vencimento1aParcela,
        dataLiberacaoCredito,
        valorLiquidoLiberado: valorLiquido ?? undefined,
        valorCarencia: valorCarencia ?? undefined,
      });
      // 2) Se anexo veio, faz upload separado no /ccb/upload e vincula ao ADF.
      // A falha vai NO RESULTADO, nao num setErro: o onSuccess dispara logo em
      // seguida e troca esta tela pelo modal de sucesso, entao o erro setado
      // aqui era descartado sem ninguem ver — o operador achava que o contrato
      // assinado tinha subido. Varredura 06/08/2026.
      let avisoAnexo: string | undefined;
      if (arquivo) {
        try {
          await atlas.banco.uploadCcb(r.contrato.adf, arquivo);
        } catch (e) {
          avisoAnexo = `O contrato assinado NAO foi anexado (${(e as Error).message}). A averbacao esta valida — reenvie o arquivo em Propostas › ${r.contrato.adf}.`;
        }
      }
      return { tipo: "contrato", contrato: r.contrato, avisoAnexo };
    },
    onSuccess: (r) => {
      if (r.tipo === "lancamento") {
        // O lancamento e' mensal: o modal fica aberto, com o historico, para o
        // banco conferir o que vai na folha.
        setAvisoOk(
          `Lançamento de ${fmtBRLUi(valorParcela ?? 0)} em ${rotuloCompetencia(competencia)} gravado${r.acao === "substituir" ? " (substituiu o anterior)" : ""}. Ele vai no movimento dessa competência.`,
        );
        qc.invalidateQueries({ queryKey: ["banco", "cartao", "reserva"] });
        return;
      }
      onSuccess({ adf: r.contrato.adf, exigeSenhaServidor: r.contrato.exigeSenhaServidor, avisoAnexo: r.avisoAnexo, reservaCartao: modo === "reserva", reserva: r.reserva });
    },
    onError: (err) => setErro(formatBackendErr(err)),
  });
  const cancelar = useMutation({
    mutationFn: (c: string) => atlas.banco.cancelarLancamentoCartao(reserva!.adf, c),
    onSuccess: (_r, c) => {
      setAvisoOk(`Lançamento de ${rotuloCompetencia(c)} cancelado: não vai no movimento.`);
      qc.invalidateQueries({ queryKey: ["banco", "cartao", "reserva"] });
    },
    onError: (err) => setErro(formatBackendErr(err)),
  });

  function handleArquivo(f: File | null) {
    setErroArquivo(null);
    if (!f) { setArquivo(null); return; }
    const okExt = /\.(pdf|jpe?g|png)$/i.test(f.name);
    if (!okExt) { setErroArquivo("Formato invalido — use PDF, JPEG ou PNG."); return; }
    if (f.size > 1_048_576) { setErroArquivo(`Arquivo tem ${(f.size / 1024).toFixed(0)} KB — maximo 1 MB.`); return; }
    setArquivo(f);
  }
  const pctBr = (v: number) => v.toFixed(2).replace(".", ",");
  const caixaErro: React.CSSProperties = { padding: 10, borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 12, color: "var(--text)", lineHeight: 1.5 };
  return (
    <div style={modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget && !mut.isPending) onClose(); }}>
      <div style={{ ...modalCard, maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0 }}>{modo === "reserva" ? "Reserva de cartão" : modo === "lancamento" ? "Lançamento de cartão" : "Averbar margem"}</h3>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
              Servidor: <b>{nomeServidor}</b>
            </div>
          </div>
          {/* Quanto cabe na folha e quanto a operacao consome — a parcela e' a
              digitada pelo banco. Vermelho quando estoura a margem. */}
          <div style={{ display: "flex", gap: 10, flexShrink: 0, flexWrap: "wrap" }}>
            <div style={{ minWidth: 150, padding: "10px 14px", borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
                {modo === "lancamento" ? "Parcela contratada" : "Margem disponível"}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: "var(--emerald-500)" }}>
                {modo === "lancamento"
                  ? (reserva ? fmtBRLUi(reserva.valorParcela) : "—")
                  : (margemAtual ? fmtBRLUi(margemAtual.disponivel) : "—")}
              </div>
            </div>
            <div style={{ minWidth: 150, padding: "10px 14px", borderRadius: 10, background: excedeMargem ? "color-mix(in srgb, var(--danger-500) 12%, transparent)" : "var(--bg-elev-2)", border: `1px solid ${excedeMargem ? "var(--danger-500)" : "var(--border)"}` }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
                Valor da parcela
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: excedeMargem ? "var(--danger-500)" : "var(--text)" }}>
                {valorParcela != null && valorParcela > 0 ? fmtBRLUi(valorParcela) : "—"}
              </div>
            </div>
            <div style={{ minWidth: 150, padding: "10px 14px", borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
                {modo === "lancamento" ? "Parcela vigente" : "Total a pagar"}
              </div>
              {modo === "lancamento" ? (
                <>
                  <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: parcelasEsgotadas ? "var(--danger-500)" : "var(--text)" }}>
                    {!reserva ? "—" : parcelasEsgotadas ? "Esgotadas" : reserva.parcelasTotal > 0 ? `${parcelaDoForm} de ${reserva.parcelasTotal}` : `${parcelaDoForm ?? "—"}ª`}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>
                    {!reserva ? " " : reserva.parcelasTotal > 0
                      ? `${reserva.parcelasLancadas} lançada(s) · faltam ${reserva.parcelasFaltam ?? 0}`
                      : "reserva sem prazo"}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: "var(--text)" }}>
                    {valorTotalPagar > 0 ? fmtBRLUi(valorTotalPagar) : "—"}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>
                    {valorTotalPagar > 0 && valorParcela != null ? `${parcelas}× de ${fmtBRLUi(valorParcela)}` : " "}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {modo === "lancamento" && !reserva ? (
          <>
            <SelectOperacaoMargem value={operacao} margens={margens} reservas={reservas} onChange={(op) => { if (op !== operacao) onTrocarOperacao(op); }} />
            <div style={caixaAviso}>
              Esta consignatária não tem reserva ativa neste cartão para o servidor. O lançamento é feito sobre uma reserva:
              reserve a margem primeiro.
            </div>
            <FormActions>
              <Button variant="ghost" type="button" onClick={onClose}>Fechar</Button>
              <Button type="button" onClick={() => onTrocarOperacao(operacaoDoCartao("reserva", tipo as ProdutoCartao))}>Reservar margem →</Button>
            </FormActions>
          </>
        ) : (
        <>
        <FormGrid cols={3}>
          {modo === "lancamento" ? (
            <SelectField
              label="Competência *"
              value={competencia}
              onChange={(e) => setCompetencia(e.target.value)}
              options={
                abertas.length > 0
                  ? abertas.map((c) => ({ value: c.competencia, label: `${rotuloCompetencia(c.competencia)} — aceita até ${rotuloCorte(c.corteEm)}` }))
                  : [{ value: "", label: "Nenhuma competência aberta" }]
              }
            />
          ) : null}
          <TextField
            label="Nº do contrato no seu sistema *"
            value={numeroContratoBanco}
            onChange={(e) => setNumeroContratoBanco(e.target.value)}
            maxLength={60}
            hint="O número do contrato no seu sistema — não pode repetir"
          />
          <TextField
            label="Vencimento 1ª parcela *"
            type="date"
            value={vencimento1aParcela}
            onChange={(e) => setVencimento1aParcela(e.target.value)}
          />
          <TextField
            label="Folha 1º desconto"
            value={folha1oDesconto || "—/—"}
            readOnly
          />
          <CurrencyField
            label="Valor total financiado *"
            value={valor}
            onValueChange={setValor}
          />
          <CurrencyField
            label="Valor líquido liberado *"
            value={valorLiquido}
            onValueChange={setValorLiquido}
            hint="O que cai na conta do servidor"
            error={liquidoAcimaDoFinanciado ? "Maior que o financiado" : undefined}
          />
          <TextField
            label="Data de liberação do crédito *"
            type="date"
            value={dataLiberacaoCredito}
            onChange={(e) => setDataLiberacaoCredito(e.target.value)}
          />
          {modo === "lancamento" ? (
            <TextField
              label="Quantidade de parcelas"
              value="1"
              readOnly
              hint={reserva && reserva.parcelasTotal > 0 && parcelaDoForm != null
                ? `Uma por mês — esta é a parcela ${parcelaDoForm} de ${reserva.parcelasTotal}`
                : "Uma por mês"}
            />
          ) : (
            <NumberField
              label={modo === "reserva" ? `Quantidade de parcelas (prazo do cartão) * (${prazoMin}× a ${prazoMax}×)` : `Quantidade de parcelas * (${prazoMin}× a ${prazoMax}×)`}
              step={1}
              min={prazoMin}
              max={prazoMax}
              value={parcelasTxt}
              onChange={(e) => setParcelasTxt(e.target.value)}
              error={prazoFora ? `Entre ${prazoMin}× e ${prazoMax}×` : undefined}
              hint={modo === "reserva" ? "Cada lançamento mensal é uma parcela; a última quita a reserva" : undefined}
            />
          )}
          <CurrencyField
            label="Valor da parcela *"
            value={valorParcela}
            onValueChange={setValorParcela}
            hint={modo === "lancamento"
              ? existente
                ? `Substitui o lançamento de ${fmtBRLUi(existente.valor)}`
                : `Até ${fmtBRLUi(reserva?.valorParcela ?? 0)} (parcela contratada)`
              : modo === "reserva"
                ? `A reserva ocupa toda a margem disponível do cartão (${fmtBRLUi(margemAtual?.disponivel ?? 0)}). Este valor é o teto de cada lançamento.`
                : "Calculada no seu sistema"}
            error={excedeMargem ? (modo === "lancamento" ? "Acima da parcela contratada" : "Acima da margem disponível") : undefined}
          />
          {/* A tela do banco mostra JUROS; o CET esta na CCB. Os dois campos
              existem para que os juros nao acabem digitados no lugar do CET. */}
          <NumberField
            label={ehEmprestimo ? "Juros mensal (%) — opcional" : "Juros mensal (%) *"}
            step={0.01}
            min={0}
            value={jurosTxt}
            onChange={(e) => setJurosTxt(e.target.value)}
            hint={ehEmprestimo
              ? "A taxa de juros do seu sistema. Sem teto; o CET não pode ficar abaixo dela."
              : "Juros do rotativo. Cartão não passa pelo teto de CET do órgão."}
          />
          <NumberField
            label="CET (% a.m.) — conforme a CCB *"
            step={0.01}
            min={0}
            max={tetoCetPct ?? undefined}
            value={cetTxt}
            onChange={(e) => setCetTxt(e.target.value)}
            hint={`Custo Efetivo Total: juros + IOF + tarifas + seguro. Não é a taxa de juros.${tetoCetPct != null ? ` Teto do órgão: ${pctBr(tetoCetPct)}% a.m.` : ""}${cetAm != null && cetAm > 0 ? ` Equivale a ${pctBr(cetAnual(cetAm) * 100)}% a.a.` : ""}`}
            error={excedeCet ? "Acima do teto do órgão" : cetAbaixoJuros ? "Menor que os juros" : undefined}
          />
          <CurrencyField
            label="Valor IOF *"
            value={iof}
            onValueChange={setIof}
          />
          <NumberField
            label="Dias de carência *"
            step={1}
            min={0}
            value={diasCarenciaTxt}
            onChange={(e) => setDiasCarenciaTxt(e.target.value)}
            hint={diasEntreDatas != null ? `Entre a liberação e a 1ª parcela: ${diasEntreDatas} dias` : "Dias entre a liberação e a 1ª parcela"}
          />
          <CurrencyField
            label="Valor carência"
            value={valorCarencia}
            onValueChange={setValorCarencia}
          />
          {cartaoDisponivel ? (
            <SelectOperacaoMargem
              value={operacao}
              margens={margens}
              reservas={reservas}
              onChange={(op) => { if (op !== operacao) onTrocarOperacao(op); }}
            />
          ) : (
            <SelectField
              label="Tipo de margem"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoMargem)}
              options={margens.map((m) => ({ value: m.tipo, label: `${m.label} — disp. ${fmtBRLUi(m.disponivel)}` }))}
            />
          )}
        </FormGrid>
        <TextareaField
          label="Observações"
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          rows={2}
          maxLength={300}
        />
        {/* Upload de CCB — opcional, PDF/JPEG/PNG max 1MB (spec do cliente). O
            lancamento nao anexa: o contrato e' o da reserva. */}
        {modo !== "lancamento" ? (
        <div style={{ padding: "10px 12px", borderRadius: 8, border: "1px dashed var(--border-strong)", background: "var(--bg-elev-2)" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 6 }}>
            Anexar contrato assinado (opcional)
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
            Apenas PDF, JPEG ou PNG. Máximo 1 MB. O anexo será linkado ao ADF gerado.
          </div>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={(e) => handleArquivo(e.target.files?.[0] ?? null)}
            style={{ fontSize: 13, color: "var(--text)" }}
          />
          {arquivo ? (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--emerald-500)" }}>
              ✓ {arquivo.name} — {(arquivo.size / 1024).toFixed(1)} KB
            </div>
          ) : null}
          {erroArquivo ? (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--danger-500)" }}>{erroArquivo}</div>
          ) : null}
        </div>
        ) : null}
        {excedeCet && tetoCetPct != null && cetPct != null ? (
          <div style={caixaErro}>
            CET de <b>{pctBr(cetPct)}%</b> a.m. excede o teto de <b>{pctBr(tetoCetPct)}%</b> definido pelo órgão para este convênio.
          </div>
        ) : cetAbaixoMinimo ? (
          <div style={caixaErro}>
            CET abaixo do mínimo de <b>{pctBr(CET_MINIMO_AM * 100)}%</b> a.m. Confira a digitação: o CET é informado em percentual (1,79 para 1,79%).
          </div>
        ) : cetAbaixoJuros ? (
          <div style={caixaErro}>
            O CET não pode ficar abaixo dos juros — ele os inclui. Confira se a taxa de juros do seu sistema não foi digitada no lugar do CET (o CET está na CCB).
          </div>
        ) : liquidoAcimaDoFinanciado ? (
          <div style={caixaErro}>O valor líquido liberado não pode ser maior que o valor financiado.</div>
        ) : prazoFora ? (
          <div style={caixaErro}>
            O prazo tem de ficar entre <b>{prazoMin}×</b> e <b>{prazoMax}×</b> — faixa das suas tabelas no convênio, limitada pelo teto do órgão.
          </div>
        ) : excedeMargem && tetoParcela != null && valorParcela != null ? (
          <div style={caixaErro}>
            A parcela de <b>{fmtBRLUi(valorParcela)}</b> excede {modo === "lancamento" ? "a parcela contratada na reserva" : "a margem disponível"} (<b>{fmtBRLUi(tetoParcela)}</b>).
          </div>
        ) : parcelasEsgotadas ? (
          <div style={caixaErro}>Todas as parcelas desta reserva já foram lançadas.</div>
        ) : null}
        {avisoCoerencia && !excedeMargem ? (
          <div style={{ padding: 10, borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 10%, transparent)", border: "1px solid var(--gold-500)", fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>
            A parcela informada difere em <b>{(avisoCoerencia.diferenca * 100).toFixed(1).replace(".", ",")}%</b> da parcela pelo CET
            informado (<b>{fmtBRLUi(avisoCoerencia.parcelaPeloCet)}</b>). Confira os números — a averbação não é bloqueada por isso.
          </div>
        ) : null}
        {exigeSenhaServidor && modo !== "lancamento" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 12, borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--gold-500) 40%, transparent)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gold-500)", letterSpacing: "0.05em", textTransform: "uppercase" }}>
              O órgão exige senha do servidor
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.4 }}>
              Peça ao servidor pra digitar a senha dele aqui. Sem ela, a averbação é rejeitada.
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 }}>
              Senha do servidor
            </label>
            <input
              type="password"
              value={senhaServidor}
              onChange={(e) => setSenhaServidor(e.target.value)}
              autoComplete="off"
              style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", background: "var(--bg-elev)", color: "var(--text)", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}
            />
          </div>
        ) : null}
        {avisoOk ? (
          <div style={{ ...caixaAviso, background: "color-mix(in srgb, var(--emerald-500) 10%, transparent)", border: "1px solid var(--emerald-500)" }}>
            {avisoOk}
          </div>
        ) : null}
        {erro ? (
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 13, color: "var(--danger-500)" }}>
            {erro}
          </div>
        ) : null}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={mut.isPending}>{modo === "lancamento" ? "Fechar" : "Cancelar"}</Button>
          <Button
            type="button"
            onClick={() => { setErro(null); setAvisoOk(null); mut.mutate(); }}
            disabled={mut.isPending || faltaObrigatorio || prazoFora || excedeCet || cetAbaixoMinimo || cetAbaixoJuros || liquidoAcimaDoFinanciado || excedeMargem || parcelasEsgotadas || (exigeSenhaServidor && modo !== "lancamento" && !senhaServidor)}
          >
            {mut.isPending
              ? "Gravando..."
              : modo === "reserva"
                ? "Reservar margem →"
                : modo === "lancamento"
                  ? (existente ? "Substituir lançamento →" : "Informar lançamento →")
                  : "Averbar →"}
          </Button>
        </FormActions>
        {modo === "lancamento" && reserva && reserva.lancamentos.length > 0 ? (
          <HistoricoLancamentos
            lancamentos={reserva.lancamentos}
            parcelasTotal={reserva.parcelasTotal}
            cancelando={cancelar.isPending}
            onCancelar={(c) => { setErro(null); setAvisoOk(null); cancelar.mutate(c); }}
          />
        ) : null}
        </>
        )}
      </div>
    </div>
  );
}

function AverbarSucessoModal({
  adf,
  exigeSenhaServidor,
  avisoAnexo,
  reservaCartao,
  onClose,
}: {
  adf: string;
  exigeSenhaServidor: boolean;
  /** Preenchido quando o contrato foi criado mas o upload do CCB falhou. */
  avisoAnexo?: string;
  /** A operacao foi uma reserva de cartao (ADR-0014). */
  reservaCartao?: boolean;
  onClose: () => void;
}) {
  return (
    <div style={modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, color: "var(--emerald-500)" }}>{reservaCartao ? "Reserva de cartão ativa" : "Averbação ativa"}</h3>
        <div style={{ fontSize: 14, color: "var(--text-muted)" }}>
          {reservaCartao ? "Reserva" : "Contrato"} <code style={{ fontFamily: "var(--font-mono)" }}>{adf}</code> criado.
        </div>
        {reservaCartao ? (
          <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
            Toda a margem disponível do cartão ficou reservada — para as outras consignatárias ela aparece zerada — e a
            reserva <b>não vai para a folha</b>. Informe o valor de cada competência como <b>lançamento</b>, até a
            parcela contratada — é ele que a folha desconta. Ao fechar, você continua na ficha do servidor.
          </div>
        ) : null}
        <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
          {exigeSenhaServidor ? (
            <>O servidor autorizou a operação com a senha dele. Contrato entrou <b>Ativo</b> — a averbação segue o fluxo normal de folha.</>
          ) : (
            <>O órgão não exige senha do servidor pra esta operação — o contrato entrou direto como <b>Ativo</b> e a averbação segue o fluxo normal de folha.</>
          )}
        </div>
        {avisoAnexo ? (
          <div style={{ padding: "10px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 12%, transparent)", border: "1px solid var(--gold-500)", fontSize: 13, color: "var(--text)", lineHeight: 1.5 }}>
            ⚠ {avisoAnexo}
          </div>
        ) : null}
        <FormActions>
          <Button type="button" onClick={onClose}>OK</Button>
        </FormActions>
      </div>
    </div>
  );
}
